import { useState } from 'react'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'
import CheckIcon from '@mui/icons-material/Check'
import BlockIcon from '@mui/icons-material/Block'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import CancelIcon from '@mui/icons-material/Cancel'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import EventBusyIcon from '@mui/icons-material/EventBusy'

import { MeetingLinkOrder } from '@/core/models/meeting-link-order.model'
import { OrderStatus } from '@/core/models/order.model'
import { useAcceptMeetingLinkOrderMutation, useRefuseMeetingLinkOrderMutation, useUpdateMeetingLinkOrderMutation } from '@/core/services/order'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

import { getApiErrorMessage } from '@/utils/errors'
import { OrderPaymentBadge } from '@/pages/meeting-link/components/OrderPaymentBadge'

import OrderStatusStepper from './OrderStatusStepper'
import StartConsultationDialog from './StartConsultationDialog'

type AppointmentDetailPanelProps = {
  order: MeetingLinkOrder | null
  open: boolean
  onClose: () => void
}

export default function AppointmentDetailPanel(props: AppointmentDetailPanelProps) {
  const { order, open, onClose } = props
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const authUser = useAppSelector(selectAuthUser)

  const [acceptOrder] = useAcceptMeetingLinkOrderMutation()
  const [refuseOrder] = useRefuseMeetingLinkOrderMutation()
  const [updateOrder] = useUpdateMeetingLinkOrderMutation()

  const [startDialogOpen, setStartDialogOpen] = useState(false)

  if (!order) return null

  const patientName = order.createdBy
    ? `${order.createdBy.userInfo?.name} ${order.createdBy.userInfo?.surname}`
    : '-'

  const handleAccept = async () => {
    try {
      await acceptOrder(order._id).unwrap()
      enqueueSnackbar(t('page.appointments.notification.accepted'), { variant: 'success' })
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('page.appointments.notification.acceptError')), { variant: 'error' })
    }
  }

  const handleRefuse = async () => {
    try {
      await refuseOrder(order._id).unwrap()
      enqueueSnackbar(t('page.appointments.notification.refused'), { variant: 'success' })
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('page.appointments.notification.refuseError')), { variant: 'error' })
    }
  }

  const handleCancel = async () => {
    try {
      await updateOrder({ id: order._id, status: OrderStatus.Canceled }).unwrap()
      enqueueSnackbar(t('page.appointments.notification.canceled'), { variant: 'success' })
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('page.appointments.notification.cancelError')), { variant: 'error' })
    }
  }

  const handleConfirm = async () => {
    try {
      await updateOrder({ id: order._id, status: OrderStatus.Confirmed }).unwrap()
      enqueueSnackbar(t('page.appointments.notification.confirmed'), { variant: 'success' })
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('page.appointments.notification.confirmError')), { variant: 'error' })
    }
  }

  const handleMarkNoShow = async () => {
    try {
      await updateOrder({ id: order._id, status: OrderStatus.No_Show }).unwrap()
      enqueueSnackbar(t('page.appointments.notification.noShow'), { variant: 'success' })
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('page.appointments.notification.noShowError')), { variant: 'error' })
    }
  }

  const handleComplete = async () => {
    try {
      await updateOrder({ id: order._id, status: OrderStatus.Completed }).unwrap()
      enqueueSnackbar(t('page.appointments.notification.completed'), { variant: 'success' })
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('page.appointments.notification.completeError')), { variant: 'error' })
    }
  }

  const handleOpenConsultation = () => {
    if (order.consultation) {
      navigate(`/consultations/${order.consultation._id}`)
    }
  }

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 420, maxWidth: '100vw' } }}>
      <Stack sx={{ height: '100%' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 2 }}>
          <Typography variant="h6">{t('page.appointments.detail.title')}</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>

        <Divider />

        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          <Stack spacing={3}>
            {/* Patient info */}
            <Box>
              <Typography variant="overline" color="text.secondary">
                {t('page.appointments.detail.patient')}
              </Typography>
              <Typography variant="h6">{patientName}</Typography>
            </Box>

            {/* Appointment details */}
            <Box>
              <Typography variant="overline" color="text.secondary">
                {t('page.appointments.detail.details')}
              </Typography>
              <Stack spacing={1}>
                <DetailRow label={t('page.appointments.detail.date')} value={dayjs(order.startDate).format('DD/MM/YYYY HH:mm')} />
                <DetailRow label={t('page.appointments.detail.speciality')} value={order.speciality} />
                <DetailRow label={t('page.appointments.detail.meetingPlace')} value={order.meetingPlace?.description || '-'} />
                {order.meetingPlace?.apartment && (
                  <DetailRow label={t('page.appointments.detail.apartment')} value={order.meetingPlace.apartment} />
                )}
                {order.meetingPlace?.floor && (
                  <DetailRow label={t('page.appointments.detail.floor')} value={order.meetingPlace.floor} />
                )}
                {order.meetingPlace?.doorCode && (
                  <DetailRow label={t('page.appointments.detail.doorCode')} value={order.meetingPlace.doorCode} />
                )}
                {order.meetingPlace?.instructions && (
                  <DetailRow label={t('page.appointments.detail.accessInstructions')} value={order.meetingPlace.instructions} />
                )}
                <DetailRow label={t('page.appointments.detail.referenceId')} value={order.referenceId} />
              </Stack>
            </Box>

            {/* Status stepper */}
            <Box>
              <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                {t('page.appointments.detail.status')}
              </Typography>
              <OrderStatusStepper status={order.status} />
              {order.status === OrderStatus.Canceled && order.cancelReason && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {order.cancelReason}
                </Typography>
              )}
            </Box>

            {/* Payment */}
            <Box>
              <Typography variant="overline" color="text.secondary">
                {t('page.appointments.detail.payment')}
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <OrderPaymentBadge
                  payment={order.payment}
                  requiresPayment={order.requiresPayment}
                  paymentCompleted={order.paymentCompleted}
                />
                {!order.requiresPayment && (
                  <Typography variant="body2" color="text.secondary">
                    {t('page.appointments.detail.noPaymentRequired')}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Files */}
            {order.createdBy && (
              <Box>
                <Typography variant="overline" color="text.secondary">
                  {t('page.appointments.detail.documents')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('page.appointments.detail.viewPatientProfile')}
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>

        <Divider />

        {/* Action buttons */}
        <Stack direction="row" spacing={1} sx={{ p: 2, flexWrap: 'wrap', gap: 1 }}>
          {order.status === OrderStatus.Pending && (
            <>
              <Button variant="contained" color="success" startIcon={<CheckIcon />} onClick={handleAccept}>
                {t('page.appointments.action.accept')}
              </Button>
              <Button variant="outlined" color="error" startIcon={<BlockIcon />} onClick={handleRefuse}>
                {t('page.appointments.action.refuse')}
              </Button>
            </>
          )}
          {order.status === OrderStatus.Accepted && (
            <>
              <Button variant="contained" color="success" startIcon={<CheckIcon />} onClick={handleConfirm}>
                {t('page.appointments.action.confirm')}
              </Button>
              <Button variant="contained" color="primary" startIcon={<PlayArrowIcon />} onClick={() => setStartDialogOpen(true)}>
                {t('page.appointments.action.startConsultation')}
              </Button>
              <Button variant="outlined" color="error" startIcon={<CancelIcon />} onClick={handleCancel}>
                {t('page.appointments.action.cancel')}
              </Button>
            </>
          )}
          {order.status === OrderStatus.Confirmed && (
            <>
              <Button variant="contained" color="primary" startIcon={<PlayArrowIcon />} onClick={() => setStartDialogOpen(true)}>
                {t('page.appointments.action.startConsultation')}
              </Button>
              <Button variant="outlined" color="warning" startIcon={<EventBusyIcon />} onClick={handleMarkNoShow}>
                {t('page.appointments.action.noShow')}
              </Button>
              <Button variant="outlined" color="error" startIcon={<CancelIcon />} onClick={handleCancel}>
                {t('page.appointments.action.cancel')}
              </Button>
            </>
          )}
          {order.status === OrderStatus.In_Progress && (
            <>
              <Button variant="contained" color="success" startIcon={<DoneAllIcon />} onClick={handleComplete}>
                {t('page.appointments.action.complete')}
              </Button>
              {order.consultation && (
                <Button variant="outlined" color="primary" startIcon={<OpenInNewIcon />} onClick={handleOpenConsultation}>
                  {t('page.appointments.action.openConsultation')}
                </Button>
              )}
            </>
          )}
          {order.status === OrderStatus.Consultation_Created && (
            <>
              {order.consultation && (
                <Button variant="contained" color="primary" startIcon={<OpenInNewIcon />} onClick={handleOpenConsultation}>
                  {t('page.appointments.action.openConsultation')}
                </Button>
              )}
              <Button variant="contained" color="success" startIcon={<DoneAllIcon />} onClick={handleComplete}>
                {t('page.appointments.action.complete')}
              </Button>
            </>
          )}
        </Stack>
      </Stack>
      <StartConsultationDialog
        open={startDialogOpen}
        onClose={() => setStartDialogOpen(false)}
        order={order}
      />
    </Drawer>
  )
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {value}
      </Typography>
    </Stack>
  )
}
